import { LoadingOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MyImage } from '../../components/MyImage/MyImage.tsx';
import Navbar from '../../components/Navbar/Navbar.tsx';
import { type VersionInfo, loadVersionInfo } from '../../components/Version/Version.tsx';
import { envApiUrl } from '../../fetchHooks/envApiUrl';
import { useUserProfile } from '../../fetchHooks/useUserProfile.ts';
import FontSelector from './FontSelector.tsx';
import UploadProfilePicture from './UploadProfilePicture.jsx';
import UserInputDebounced from './UserInputDebounced.tsx';
import UserServicesCheckbox from './UserServicesCheckbox.tsx';

const ProfilePage = () => {
  const { t } = useTranslation();
  const { data: userProfile, isLoading } = useUserProfile();
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadVersionInfo(setVersionInfo, setLoading);
  }, []);

  if (isLoading || loading) return <LoadingOutlined />;

  return (
    <>
      <Navbar />
      <div className="app-container">
        <h1>{t('profile.title')}</h1>
        <div style={{ display: 'flex' }}>
          <div style={{ margin: '2rem' }}>
            {(userProfile?.settings?.profilePictureFileId || userProfile?.settings?.profilePicURL) && (
              <MyImage
                description={t('profile.profilePicture')}
                src={
                  userProfile?.settings?.profilePictureFileId
                    ? `${envApiUrl}/files/image/${userProfile.settings.profilePictureFileId}`
                    : userProfile?.settings?.profilePicURL || ''
                }
              />
            )}
            <UploadProfilePicture />
          </div>
          <div style={{ margin: '2rem' }}>
            <FontSelector
              userId={userProfile?.id}
              currentFont={userProfile?.settings?.preferredFont}
              currentSettings={userProfile?.settings}
            />
            <UserInputDebounced
              userId={userProfile?.id}
              label={t('profile.firstname')}
              propertyName="firstname"
              value={userProfile?.firstname}
              type="text"
            />
            <UserInputDebounced
              userId={userProfile?.id}
              label={t('profile.lastname')}
              propertyName="lastname"
              value={userProfile?.lastname}
              type="text"
            />
            <UserInputDebounced
              userId={userProfile?.id}
              label={t('profile.hourRate')}
              propertyName="hourRate"
              value={userProfile?.hourRate}
              type="number"
            />
            <UserInputDebounced
              userId={userProfile?.id}
              label={t('profile.dateOfBirth')}
              propertyName="dateOfBirth"
              value={userProfile?.dateOfBirth ? new Date(userProfile?.dateOfBirth).toISOString().split('T')[0] : ''}
              type="date"
            />
            <UserInputDebounced
              userId={userProfile?.id}
              label={t('profile.gender')}
              propertyName="gender"
              value={userProfile?.gender}
              type="text"
            />
            <UserInputDebounced
              userId={userProfile?.id}
              label={t('profile.nationality')}
              propertyName="nationality"
              value={userProfile?.nationality}
              type="text"
            />
            <UserInputDebounced
              userId={userProfile?.id}
              label={t('profile.language')}
              propertyName="language"
              value={userProfile?.language}
              type="text"
              disabled
            />
            <UserInputDebounced
              userId={userProfile?.id}
              label={t('profile.height')}
              propertyName="height"
              value={userProfile?.height}
              type="number"
              description={t('profile.heightDescription')}
            />
            <UserInputDebounced
              userId={userProfile?.id}
              label={t('profile.weight')}
              propertyName="weight"
              value={userProfile?.weight}
              type="number"
              description={t('profile.weightDescription')}
            />
            <UserInputDebounced
              userId={userProfile?.id}
              label={t('profile.healthConditions')}
              propertyName="healthConditions"
              value={userProfile?.healthConditions}
              textField
            />
            <UserInputDebounced
              userId={userProfile?.id}
              label={t('profile.bloodType')}
              propertyName="bloodType"
              value={userProfile?.bloodType}
              type="text"
            />
            <UserInputDebounced
              userId={userProfile?.id}
              label={t('common.email')}
              propertyName="email"
              value={userProfile?.email}
              type="text"
            />
            <UserServicesCheckbox userId={userProfile?.id} services={userProfile?.services} />
          </div>
        </div>
        <i style={{ fontSize: '12px' }}>
          Version: v{versionInfo?.version} (
          {versionInfo?.buildDate ? new Date(versionInfo.buildDate).toLocaleString() : 'Unknown date'})
        </i>
      </div>
    </>
  );
};

export default ProfilePage;
